import type { DBPage } from '../db/db';
import { db } from '../db/db';
import { initializeSearchIndex, searchPages } from '../services/pageSearchIndex';
import { cosineSimilarity } from '../store/pageSimilarity';
import { logger } from '../utils/logger';

const WS_URL = 'ws://127.0.0.1:7878';
const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

interface CompactPage {
  url: string;
  originalUrl: string;
  title?: string;
  favIconUrl?: string;
  visible: 'active' | 'suspended' | 'closed';
  humanTags: string[];
  autoTags: string[];
  lastActiveAt?: number;
  embeddingStatus: 'embedded' | 'pending' | 'errored';
  completionStatus?: 'pending' | 'completed';
  priority?: number;
  dueDate?: number;
  creationDate?: number;
  similarity?: number;
}

function compactPage(p: DBPage): CompactPage {
  const times = p.accessTimes ?? [];
  return {
    url: p.url,
    originalUrl: p.originalUrl,
    title: p.title,
    favIconUrl: p.favIconUrl,
    visible: p.visible,
    humanTags: p.humanTags ?? [],
    autoTags: (p.autoTags ?? []).map((t) => t.name),
    lastActiveAt: times.length > 0 ? times[times.length - 1] : undefined,
    embeddingStatus: p.embeddingErrored ? 'errored' : p.embedding ? 'embedded' : 'pending',
    completionStatus: p.completionStatus,
    priority: p.priority,
    dueDate: p.dueDate,
    creationDate: p.creationDate,
  };
}

async function dispatch(method: string, params: Record<string, unknown>): Promise<unknown> {
  switch (method) {
    case 'search_pages': {
      const query = String(params['query'] ?? '');
      const limit = Number(params['limit'] ?? 20);
      const tokens = query
        .toLowerCase()
        .split(/\s+/)
        .filter((t) => t.length > 0);
      const urls = searchPages(tokens).slice(0, limit);
      const pages = await db.pages.bulkGet(urls);
      return pages.filter((p): p is DBPage => p != null).map(compactPage);
    }

    case 'get_page': {
      const url = String(params['url'] ?? '');
      const page = await db.pages.get(url);
      return page ? compactPage(page) : null;
    }

    case 'list_tags': {
      const tags = await db.tags.toArray();
      const result = await Promise.all(
        tags.map(async (tag) => ({
          name: tag.name,
          pageCount: await db.pages.where('humanTags').equals(tag.name).count(),
        })),
      );
      return result.sort((a, b) => b.pageCount - a.pageCount);
    }

    case 'get_pages_by_tag': {
      const tag = String(params['tag'] ?? '');
      const limit = Number(params['limit'] ?? 100);
      const pages = await db.pages.where('humanTags').equals(tag).limit(limit).toArray();
      return pages.map(compactPage);
    }

    case 'find_similar': {
      const url = String(params['url'] ?? '');
      const limit = Number(params['limit'] ?? 10);
      const target = await db.pages.get(url);
      if (!target) throw new Error(`Page not found: ${url}`);
      if (!target.embedding)
        throw new Error(
          `No embedding for ${url}: page not yet embedded — cannot compute similarity`,
        );

      const allUrls = (await db.pages.toCollection().primaryKeys()) as string[];
      const scored: { url: string; score: number }[] = [];

      for (let i = 0; i < allUrls.length; i += 500) {
        const batch = await db.pages.bulkGet(allUrls.slice(i, i + 500));
        for (const p of batch) {
          if (!p || p.url === url || !p.embedding) continue;
          scored.push({ url: p.url, score: cosineSimilarity(target.embedding!, p.embedding) });
        }
      }

      scored.sort((a, b) => b.score - a.score);
      const topN = scored.slice(0, limit);
      const topPages = await db.pages.bulkGet(topN.map((r) => r.url));
      return topN
        .map((r, i) => {
          const p = topPages[i];
          return p ? { ...compactPage(p), similarity: r.score } : null;
        })
        .filter(Boolean);
    }

    case 'recent_pages': {
      const limit = Number(params['limit'] ?? 20);
      // lastActiveDesc is -timestamp; ascending order = most-recent first
      const pages = await db.pages.orderBy('lastActiveDesc').limit(limit).toArray();
      return pages.map(compactPage);
    }

    case 'get_stats': {
      const pageCount = await db.pages.count();
      const tagCount = await db.tags.count();
      const embeddedCount = await db.pages.filter((p) => !!p.embedding).count();
      return { pageCount, tagCount, embeddedCount };
    }

    default:
      throw new Error(`Unknown MCP method: ${method}`);
  }
}

let ws: WebSocket | null = null;
let backoffMs = MIN_BACKOFF_MS;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;

function connect(): void {
  if (stopped) return;
  try {
    ws = new WebSocket(WS_URL);
  } catch {
    scheduleReconnect();
    return;
  }

  ws.addEventListener('open', () => {
    logger.info('[MCP] Connected to bridge at', WS_URL);
    backoffMs = MIN_BACKOFF_MS;
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    let req: { id: string; method: string; params: Record<string, unknown> };
    try {
      req = JSON.parse(event.data as string) as {
        id: string;
        method: string;
        params: Record<string, unknown>;
      };
    } catch {
      return;
    }
    dispatch(req.method, req.params ?? {})
      .then((result) => ws?.send(JSON.stringify({ id: req.id, result })))
      .catch((e: unknown) =>
        ws?.send(
          JSON.stringify({
            id: req.id,
            error: { code: 'HANDLER_ERROR', message: e instanceof Error ? e.message : String(e) },
          }),
        ),
      );
  });

  ws.addEventListener('close', () => {
    ws = null;
    if (!stopped) scheduleReconnect();
  });

  // errors are always followed by close; handle there
  ws.addEventListener('error', () => undefined);
}

function scheduleReconnect(): void {
  if (stopped) return;
  logger.debug('[MCP] Bridge not reachable, retrying in', backoffMs, 'ms');
  reconnectTimer = setTimeout(connect, backoffMs);
  backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
}

export function startMcpDevClient(): void {
  logger.info('[MCP] Starting dev-mode MCP bridge client');
  initializeSearchIndex().catch((e: unknown) =>
    logger.warn('[MCP] Search index init failed:', e),
  );
  connect();
}

export function stopMcpDevClient(): void {
  stopped = true;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  ws?.close();
  ws = null;
}
